import React, { useEffect, useRef, useState } from 'react';
import { useQuery } from 'react-query';
import { Alert, Button, Card, LoadingState } from './ui';
import { useAuth } from '../context/AuthContext';
import accountService from '../services/accountService';

const settings = [
  ['last_seen', 'Онлайн и последний визит'],
  ['profile_photo', 'Фото профиля'],
  ['phone_number', 'Номер телефона'],
  ['forwarded_messages', 'Ссылка на аккаунт в пересланных сообщениях'],
  ['calls', 'Кто может звонить'],
  ['group_invites', 'Кто может добавлять в группы'],
];
const audiences = { everyone: 'Все', contacts: 'Мои контакты', nobody: 'Никто' };
const errorMessage = error => error?.response?.data?.message || 'Не удалось связаться с Telegram. Попробуйте загрузить настройки заново.';

export default function AccountPrivacySettings({ account, authInProgress = false }) {
  const { user } = useAuth();
  const [privacy, setPrivacy] = useState(null);
  const [draft, setDraft] = useState({});
  const [consent, setConsent] = useState({});
  const [saving, setSaving] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [uncertain, setUncertain] = useState(false);
  const mounted = useRef(true);
  const savingRef = useRef(false);
  const privacyRef = useRef(null);
  const draftRef = useRef({});
  const canManage = ['admin', 'manager'].includes(user?.role);
  const supported = ['agent', 'monitoring', 'ai'].includes(account.type);
  const connected = account.status === 'on' && account.has_session === true && account.live_status !== 'offline' && !authInProgress;
  const editable = canManage && supported && connected;

  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);

  const query = useQuery(['account-privacy', account.id], async () => {
    const data = await accountService.getPrivacy(account.id);
    if (String(data?.account_id) !== String(account.id) || !data?.privacy || settings.some(([key]) => ![...Object.keys(audiences), 'custom'].includes(data.privacy[key]?.preset))) {
      throw new Error('Incomplete privacy settings');
    }
    return data.privacy;
  }, {
    enabled: editable && privacy === null && !saving,
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    cacheTime: 0,
    onSuccess: next => {
      if (!mounted.current) return;
      const nextDraft = {};
      settings.forEach(([key]) => {
        const previous = draftRef.current[key];
        const dirty = previous && previous !== privacyRef.current?.[key]?.preset;
        nextDraft[key] = dirty ? previous : next[key].preset;
      });
      privacyRef.current = next;
      draftRef.current = nextDraft;
      setPrivacy(next);
      setDraft(nextDraft);
      setConsent({});
      setUncertain(false);
    },
  });

  const refresh = () => {
    if (savingRef.current || query.isFetching || !editable) return;
    setError('');
    setSuccess('');
    query.refetch();
  };

  const save = async (setting) => {
    const preset = draftRef.current[setting];
    const current = privacyRef.current?.[setting]?.preset;
    if (savingRef.current || query.isFetching || query.isError || !editable || uncertain || !current || !audiences[preset] || preset === current || (current === 'custom' && !consent[setting])) return;
    savingRef.current = true;
    setSaving(setting);
    setError('');
    setSuccess('');
    try {
      const result = await accountService.updatePrivacy(account.id, {
        setting, preset, ...(current === 'custom' ? { replace_custom: true } : {}),
      });
      if (!mounted.current) return;
      if (String(result?.account_id) !== String(account.id) || result?.setting !== setting || result?.preset !== preset) {
        setUncertain(true);
        return;
      }
      const next = { ...privacyRef.current, [setting]: { preset: result.preset } };
      privacyRef.current = next;
      setPrivacy(next);
      setConsent(value => ({ ...value, [setting]: false }));
      setSuccess('Настройка сохранена и проверена в Telegram.');
    } catch (saveError) {
      if (!mounted.current) return;
      const code = saveError?.response?.data?.code;
      setError(errorMessage(saveError));
      if (code === 'PRIVACY_CUSTOM_RULES') {
        setConsent({});
        await query.refetch();
      } else if (code === 'PRIVACY_UPDATE_UNCERTAIN' || !saveError?.response || saveError.response.status >= 500) {
        setUncertain(true);
      }
    } finally {
      savingRef.current = false;
      if (mounted.current) setSaving(null);
    }
  };

  const hasChanges = settings.some(([key]) => draft[key] && draft[key] !== privacy?.[key]?.preset);
  const disabled = !editable || query.isFetching || query.isError || Boolean(saving) || uncertain;

  return (
    <Card title="Приватность Telegram" className="mb-6">
      <div className="mb-4 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-sm text-gray-500">Выберите, кому доступны данные и действия. Сохраняйте каждую настройку отдельно.</p>
        <Button size="sm" variant="secondary" disabled={!editable || query.isFetching || Boolean(saving)} onClick={refresh}>Обновить из Telegram</Button>
      </div>
      {!canManage && <p className="text-sm text-gray-500">Настройки доступны администратору и руководителю.</p>}
      {canManage && !supported && <p className="text-sm text-gray-500">Приватность доступна для аккаунтов агента, мониторинга и ИИ.</p>}
      {canManage && supported && !connected && <p className="text-sm text-gray-500">Включите аккаунт и завершите подключение Telegram.</p>}
      {query.isFetching && <LoadingState message="Загружаем настройки из Telegram…" />}
      {query.isError && editable && <div role="alert" className="mb-3"><Alert variant="error">{errorMessage(query.error)}</Alert><Button size="sm" variant="secondary" className="mt-2" disabled={query.isFetching || Boolean(saving)} onClick={refresh}>Повторить загрузку</Button></div>}
      {error && <div role="alert" className="mb-3"><Alert variant="error">{error}</Alert></div>}
      {uncertain && <Alert variant="warning" className="mb-3">Изменение могло примениться. Обновите настройки из Telegram перед повторным сохранением.</Alert>}
      {success && <div role="status" className="mb-3"><Alert variant="success">{success}</Alert></div>}
      {privacy && canManage && supported && <div className="divide-y divide-gray-100">
        {settings.map(([key, label]) => {
          const custom = privacy[key]?.preset === 'custom';
          const changed = draft[key] !== privacy[key]?.preset;
          const inputId = `privacy-${account.id}-${key}`;
          return (
            <div key={key} className="py-3 first:pt-0 last:pb-0">
              <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                <label htmlFor={inputId} className="min-w-0 flex-1 text-sm font-medium text-gray-700">{label}</label>
                <div className="flex flex-wrap items-center gap-2 sm:flex-nowrap">
                  <select id={inputId} value={draft[key] || ''} disabled={disabled} onChange={event => {
                    const next = { ...draftRef.current, [key]: event.target.value };
                    draftRef.current = next;
                    setDraft(next);
                    setConsent(value => ({ ...value, [key]: false }));
                    setSuccess('');
                  }} className="min-w-0 flex-1 rounded-md border-gray-300 text-sm shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:w-52">
                    {custom && <option value="custom" disabled>С исключениями</option>}
                    {Object.entries(audiences).map(([value, title]) => <option key={value} value={value}>{title}</option>)}
                  </select>
                  <Button size="sm" disabled={disabled || !changed || !audiences[draft[key]] || (custom && !consent[key])} isLoading={saving === key} onClick={() => save(key)} aria-label={`Сохранить: ${label}`}>Сохранить</Button>
                </div>
              </div>
              {custom && <p className="mt-2 text-xs text-gray-500">В Telegram настроены индивидуальные правила или исключения.</p>}
              {custom && changed && <label className="mt-2 flex items-start gap-2 text-sm text-gray-700"><input type="checkbox" className="mt-0.5 rounded border-gray-300" checked={Boolean(consent[key])} disabled={disabled} onChange={event => setConsent(value => ({ ...value, [key]: event.target.checked }))} /><span>Заменить все текущие правила и исключения для «{label}» выбранным вариантом.</span></label>}
            </div>
          );
        })}
      </div>}
      {hasChanges && <p className="mt-3 text-xs text-gray-500">Есть несохранённые изменения. Обновление из Telegram сохранит ваш выбор в форме.</p>}
      <p className="mt-3 text-xs text-gray-500">Ограничение последнего визита может также ограничить видимость чужого статуса. Telegram применяет свои правила отображения онлайна.</p>
    </Card>
  );
}
